import React, { useEffect, useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Menu,
  MenuItem,
  Paper,
  Badge,
  Container,
  Stack,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Dialog,
  InputAdornment,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  alpha,
  Alert as MuiAlert,  // Rename to avoid conflict
  Skeleton,
  Divider
} from '@mui/material';
import { ListItemIcon, ListItemText } from '@mui/material';
import {
  Add as AddIcon,
  SmartToy as AIIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  BarChart as StatsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Delete as DeleteOutlineIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon, // Added AnalyticsIcon
  ContentCopy as CloneIcon,
  MenuBook as DocumentationIcon,
  Article as TemplateIcon,
  ChatBubbleOutline as ResponsesIcon, CalendarToday as CalendarIcon
  , PeopleAlt as PeopleIcon, Poll as PollIcon, TrendingUp as TrendingUpIcon, Search as SearchIcon, FilterList as FilterListIcon, ViewList as ViewListIcon, Apps as AppsIcon, Menu as MenuIcon, BugReport as BugReportIcon
} from '@mui/icons-material';

// Move to top of file with other imports
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDispatch, useSelector } from 'react-redux';
import { setSurveys, deleteSurvey } from '../store/surveySlice';
import { useTranslation } from 'react-i18next';

// Components
import MetaTags from '../components/SEO/MetaTags';
import CreateSurveyModal from '../components/survey/CreateSurveyModal';
import SurveyThemeModal from '../components/survey/SurveyThemeModal';
import ShareSurveyDialog from '../components/survey/ShareSurveyDialog';
import LanguageSelector from '../components/common/LanguageSelector';
import BugReportButton from '../components/BugReportButton';
import { setClarityHost, sendClarityEvent, setClarityUser } from '../utils/clarity';

// Assets & Config
import { getApiUrl } from '../config/api';
import API_CONFIG from '../config/api';
import { useAnalytics } from '../contexts/AnalyticsContext';

// Use logo from public folder to avoid build path issues
const logo = process.env.PUBLIC_URL + '/logo.png';

const Dashboard = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { user: currentUser, logout, adminImpersonation, getCurrentUser } = useAuth();

  // Add pulse animation styles
  const skeletonStyles = `
    @keyframes pulse {
      0% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
      100% {
        opacity: 1;
      }
    }
  `;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const surveys = useSelector(state => state.survey.surveys);
  const [anchorEl, setAnchorEl] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [surveyName, setSurveyName] = useState('');
  const [selectedTheme, setSelectedTheme] = useState(null);
  const theme = useTheme();
  const isCompactActions = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState('');
  const [cloneLoading, setCloneLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // values: 'all' | 'published' | 'draft'
  const [viewMode, setViewMode] = useState('grid'); // values: 'grid' | 'list'
  const [totalCompletedResponses, setTotalCompletedResponses] = useState(0);

  useEffect(() => {
    const effectiveUser = getCurrentUser();
    if (!effectiveUser) {
      navigate('/login');
    } else {
      try {
        // register dashboard user with Clarity
        setClarityUser(null, effectiveUser?.uid || effectiveUser?.id || null);
      } catch (e) {
        console.warn('Failed to set clarity user', e);
      }
      fetchSurveys();
    }
  }, [currentUser, adminImpersonation, navigate, getCurrentUser]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      setError(null);
      const effectiveUser = getCurrentUser();
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.LIST) + `?user_id=${effectiveUser.uid}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch surveys');
      }

      dispatch(setSurveys(data.surveys || []));

      // Fetch completed responses count for all surveys
      await fetchTotalCompletedResponses(data.surveys || []);
    } catch (err) {
      console.error('Error fetching surveys:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalCompletedResponses = async (surveysList) => {
    try {
      let totalCompleted = 0;
      const updatedSurveys = [];

      // Fetch both completed and total responses for each survey
      for (const survey of surveysList) {
        try {
          // Fetch completed responses
          const completedResponse = await fetch(
            getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_RESPONSES}?survey_id=${survey.id}&status=completed`)
          );
          const completedData = await completedResponse.json();
          const completedCount = completedData.status === 'success' ? (completedData.data.responses?.length || 0) : 0;

          // Fetch all responses (for total count)
          const allResponse = await fetch(
            getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_RESPONSES}?survey_id=${survey.id}`)
          );
          const allData = await allResponse.json();
          const totalCount = allData.status === 'success' ? (allData.data.responses?.length || 0) : 0;

          totalCompleted += completedCount;

          // Add both counts to survey object
          updatedSurveys.push({
            ...survey,
            completed_responses_count: completedCount,
            total_responses_count: totalCount
          });
        } catch (error) {
          console.error(`Error fetching responses for survey ${survey.id}:`, error);
          // Add survey with 0 responses if error
          updatedSurveys.push({
            ...survey,
            completed_responses_count: 0,
            total_responses_count: 0
          });
        }
      }

      setTotalCompletedResponses(totalCompleted);
      // Update surveys with response counts
      dispatch(setSurveys(updatedSurveys));
    } catch (error) {
      console.error('Error fetching total completed responses:', error);
    }
  };

  const decodeHtmlEntities = (text) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString();
    } catch (e) {
      return iso;
    }
  };

  // FILTERED surveys
  const filteredSurveys = surveys
    .filter(s => {
      // filter by status
      if (filter === 'published') return s.status === 'published';
      if (filter === 'draft') return s.status === 'draft';
      return true;
    })
    .filter(s => {
      // search by name (case-insensitive)
      if (!searchQuery) return true;
      const name = decodeHtmlEntities(s.name || '').toLowerCase();
      return name.includes(searchQuery.toLowerCase());
    });

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleCreateSurvey = () => {
    setCreateModalOpen(true);
  };

  const handleSurveyOptionSelect = (optionId) => {
    setCreateModalOpen(false);
    if (optionId === 'blank') {
      setThemeModalOpen(true);
    } else if (optionId === 'ai') {
      // Navigate to AI survey creation
      navigate('/survey/create-with-ai');
    } else {
      navigate('/surveys/templates');
    }
  };

  const handleThemeSelect = (themeObject) => {
    setThemeModalOpen(false);
    setSelectedTheme(themeObject);
    setNameModalOpen(true);
    // setSelectedTheme(themeId);
  };

  const handleCreateNewSurvey = async () => {
    try {
      // Create survey API call here
      const effectiveUser = getCurrentUser();
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.CREATE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: effectiveUser.uid,
          name: surveyName,
          theme: selectedTheme // Don't JSON.stringify here since it's already an object
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create survey');
      }

      try {
        setClarityHost(effectiveUser);
        sendClarityEvent('survey_created', {
          surveyId: data.survey_id,
          userId: effectiveUser.uid,
          name: surveyName,
          timestamp: new Date().toISOString()
        });
      } catch (e) {
        console.warn('Clarity error while reporting new survey', e);
      }

      // Navigate to the survey editor
      navigate(`/survey/edit/${data.survey_id}`);
    } catch (err) {
      console.error('Error creating survey:', err);
      setError(err.message);
    } finally {
      setNameModalOpen(false);
      setSurveyName('');
      setSelectedTheme(null);
    }
  };

  const handleDeleteClick = (survey) => {
    setSurveyToDelete(survey);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (surveyToDelete) {
      try {
        const effectiveUser = getCurrentUser();
        const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.DELETE), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            survey_id: surveyToDelete.id,
            user_id: effectiveUser.uid
          })
        });

        const data = await response.json();

        if (data.status === 'success') {
          // Handle successful deletion
          dispatch(deleteSurvey(surveyToDelete.id));
          setDeleteDialogOpen(false);
          handleToast('Survey deleted successfully', 'success');
        } else {
          throw new Error(data.message);
        }
      } catch (error) {
        console.error('Error deleting survey:', error);
        handleToast(error.message || 'Failed to delete survey', 'error');
      }
    }
  };

  const handleToast = (message, severity = 'success') => {
    let translatedMessage = message;

    // Check if the message is a known notification key
    if (message === 'Survey deleted successfully') {
      translatedMessage = t('dashboard:notifications.surveyDeleted');
    } else if (message === 'Survey created successfully') {
      translatedMessage = t('dashboard:notifications.surveyCreated');
    } else if (message === 'Link copied to clipboard') {
      translatedMessage = t('dashboard:notifications.linkCopied');
    } else if (message === 'Survey cloned but questions could not be copied') {
      translatedMessage = 'Survey cloned but questions could not be copied';
    }

    setToast({
      open: true,
      message: translatedMessage,
      severity
    });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast(prev => ({ ...prev, open: false }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return {
          bg: 'success.main',

          color: 'white',
          border: 'success.main'
        };
      case 'draft':
        return {
          bg: '#F5F5F5',
          color: 'text.secondary',
          border: 'grey.400'
        };
      default:
        return {
          bg: 'warning.light',
          color: 'warning.main',
          border: 'warning.main'
        };
    }
  };

  const handleShareClick = (survey) => {
    // Add user_id to the survey object
    setSelectedSurvey({
      ...survey,
      user_id: currentUser.uid // Add the current user's ID
    });
    setShareDialogOpen(true);
  };

  const handleCreateWithAI = () => {
    navigate('/survey/create-with-ai');
  };


  const calculateAvgResponseRate = (surveysList) => {
    if (!surveysList || surveysList.length === 0) return 0;
    
    // Calculate total responses across all surveys
    const totalResponses = surveysList.reduce((acc, survey) => {
      return acc + (survey.completed_responses_count || 0);
    }, 0);
    
    // Calculate average: total responses / total surveys
    const avgResponseRate = totalResponses / surveysList.length;
    
    return Math.round(avgResponseRate);
  };

  const handleCloneConfirm = async () => {
    if (!cloneSourceId) return;
    try {
      setCloneLoading(true);
      const effectiveUser = getCurrentUser();
      const source = surveys.find(s => String(s.id) === String(cloneSourceId));
      if (!source) throw new Error('Source survey not found');

      const cloneName = `${decodeHtmlEntities(source.name)}(copy)`;

      // Step 1: Create the new survey
      const payload = {
        user_id: effectiveUser.uid,
        name: cloneName,
        clone_from: source.id
      };

      // Include theme (object or id) if present on source
      if (source.theme) payload.theme = source.theme;
      if (source.theme_id) payload.theme_id = source.theme_id;

      // Include settings, logic, description, metadata if available
      if (source.settings) payload.settings = source.settings;
      if (source.logic) payload.logic = source.logic;
      if (source.description) payload.description = source.description;
      if (source.metadata) payload.metadata = source.metadata;

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.CREATE), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to clone survey');
      }

      const newSurveyId = data.survey_id;

      // Step 2: Fetch questions from the source survey
      try {
        const questionsUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.QUESTIONS.LIST)}?survey_id=${source.id}`;
        const questionsResponse = await fetch(questionsUrl);

        if (!questionsResponse.ok) {
          console.warn('Failed to fetch source survey questions, survey cloned without questions');
        } else {
          const questionsData = await questionsResponse.json();

          if (questionsData.status === 'success' && questionsData.questions && questionsData.questions.length > 0) {
            // Step 3: Process and create questions for the new survey
            const questionsToCreate = questionsData.questions
              .sort((a, b) => a.order_index - b.order_index)
              .map((q, index) => {
                // Parse options if it's a string
                let parsedOptions = {};
                if (typeof q.options === 'string') {
                  try {
                    parsedOptions = JSON.parse(q.options || '{}');
                  } catch (e) {
                    console.error('Failed to parse options JSON:', e);
                  }
                } else if (q.options && typeof q.options === 'object') {
                  parsedOptions = q.options;
                }

                return {
                  survey_id: newSurveyId,
                  question_type: q.question_type,
                  question_text: q.question_text,
                  required: Boolean(q.required),
                  order_index: index,
                  options: parsedOptions
                };
              });

            // Step 4: Bulk create questions for the cloned survey
            const createQuestionsResponse = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.QUESTIONS.BULK_UPDATE), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                questions: questionsToCreate
              })
            });

            const createQuestionsResult = await createQuestionsResponse.json();
            if (createQuestionsResult.status !== 'success') {
              console.warn('Failed to clone questions:', createQuestionsResult.message);
              handleToast('Survey cloned but questions could not be copied', 'warning');
            } else {
              console.log(`Successfully cloned ${questionsToCreate.length} questions`);
            }
          }
        }
      } catch (questionError) {
        console.error('Error cloning questions:', questionError);
        // Don't fail the entire operation if questions can't be cloned
      }

      try {
        setClarityHost(effectiveUser);
        sendClarityEvent('survey_cloned', {
          originalSurveyId: source.id,
          newSurveyId: newSurveyId,
          userId: effectiveUser.uid,
          timestamp: new Date().toISOString()
        });
      } catch (e) {
        console.warn('Clarity error while reporting cloned survey', e);
      }

      handleToast('Survey created successfully', 'success');
      setCloneDialogOpen(false);
      setCloneSourceId('');
      // Refresh surveys list (best-effort) then navigate to editor
      fetchSurveys();
      navigate(`/survey/edit/${newSurveyId}`);
    } catch (err) {
      console.error('Error cloning survey:', err);
      handleToast(err.message || 'Failed to clone survey', 'error');
    } finally {
      setCloneLoading(false);
    }
  };

  // ...existing code...
  // const PlanCard = () => (
  //   <Paper
  //     elevation={0}
  //     sx={{
  //       mt: 2,
  //       mb: 4,
  //       p: 2,
  //       background: `linear-gradient(90deg, ${theme.palette.primary.lighter || theme.palette.primary.light} 0%, ${theme.palette.background.paper} 100%)`,
  //       border: '1px solid',
  //       borderColor: 'primary.main',
  //       display: 'flex',
  //       alignItems: 'center',     // vertical center

  //       width: '100%',
  //       minHeight: 64
  //     }}
  //   >
  //     <Grid container alignItems="center" spacing={3}>
  //       <Grid item xs={12} md={6}>
  //         <Box sx={{ display: 'flex', alignItems: 'center' }}>
  //           <StarIcon sx={{ mr: 1.5, color: theme.palette.warning.main }} />
  //           <Box>
  //             <Typography variant="h6" fontWeight={500} color="text.primary" sx={{ lineHeight: 1 }}>
  //               {isLoadingSubscription ? 'Loading...' :
  //                 subscriptionDetails?.planType === 'premium-plan' ? 'Premium Plan' :
  //                   subscriptionDetails?.planType === 'corporate-plan' ? 'Corporate Plan' :
  //                     'Free Plan'}
  //             </Typography>
  //             {isLoadingSubscription ? (
  //               <Typography variant="body2" color="text.secondary">
  //                 Checking subscription status...
  //               </Typography>
  //             ) : subscriptionDetails?.planType !== 'free-plan' ? (
  //               <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
  //                 Active Plan • Premium Features
  //               </Typography>
  //             ) : (
  //               <Typography variant="body2" color="text.secondary">
  //                 Free plan features active
  //               </Typography>
  //             )}
  //           </Box>
  //         </Box>
  //       </Grid>

  //       <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
  //         {isLoadingSubscription ? (
  //           <CircularProgress size={24} />
  //         ) : subscriptionDetails?.planType !== 'free-plan' ? (
  //           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
  //             {subscriptionDetails?.trialEndDate && (
  //               <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.info.main }}>
  //                 Expires on {new Date(subscriptionDetails.trialEndDate).toLocaleDateString()}
  //               </Typography>
  //             )}
  //             <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-end' }, mt: 0.5 }}>
  //               {subscriptionDetails?.created_at && (
  //                 <Typography variant="caption" color="text.secondary">
  //                   Started on {new Date(subscriptionDetails.created_at).toLocaleDateString()}
  //                 </Typography>
  //               )}
  //               {subscriptionDetails?.trialEndDate && (
  //                 <Typography variant="caption" sx={{ ml: { xs: 0, sm: 1 }, mt: { xs: 0.5, sm: 0 }, color: theme.palette.success.main, fontWeight: 500 }}>
  //                   {Math.ceil((new Date(subscriptionDetails.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
  //                 </Typography>
  //               )}
  //             </Box>
  //           </Box>
  //         ) : (
  //           <Button
  //             variant="contained"
  //             color="primary"
  //             onClick={() => navigate('/subscription')}
  //             sx={{
  //               backgroundColor: theme.palette.success.main,
  //               '&:hover': { backgroundColor: theme.palette.success.dark }
  //             }}
  //           >
  //             Upgrade to Premium
  //           </Button>
  //         )}
  //       </Grid>
  //     </Grid>
  //   </Paper>
  // );
  // // ...existing code....


  // const PlanCard = () => (
  //   <Card sx={{
  //     mb: 4,
  //     p: 0,
  //     //  p: 2.5,


  //   background: `linear-gradient(90deg, ${theme.palette.primary.lighter || theme.palette.primary.light} 0%, ${theme.palette.background.paper} 100%)`,
  //   border: '1px solid',
  //   borderColor: 'primary.main'
  //   }}>
  //      <CardContent sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 3, py: 0 }}>
  //       <Grid container alignItems="center" spacing={3}>
  //         <Grid item xs={12} md={6}>
  //           <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
  //             <StarIcon sx={{ mr: 1.5, color: theme.palette.warning.main }} />
  //             <Typography variant="h6" fontWeight={500} color="text.primary">
  //               {isLoadingSubscription ? 'Loading...' : 
  //                subscriptionDetails?.planType === 'premium-plan' ? 'Premium Plan' :
  //                subscriptionDetails?.planType === 'corporate-plan' ? 'Corporate Plan' :
  //                'Free Plan'}
  //             </Typography>
  //           </Box>
  //           {isLoadingSubscription ? (
  //             <Typography variant="body2" color="text.secondary">
  //               Checking subscription status...
  //             </Typography>
  //           ) : subscriptionDetails?.planType !== 'free-plan' ? (
  //             <Typography variant="body2" sx={{ color: theme.palette.success.main,  }}>
  //               Active Plan • Premium Features
  //             </Typography>
  //           ) : (
  //             <Typography variant="body2" color="text.secondary">
  //               Free plan features active
  //             </Typography>
  //           )}
  //         </Grid>
  //         <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
  //           {isLoadingSubscription ? (
  //             <CircularProgress size={24} />
  //           ) : subscriptionDetails?.planType !== 'free-plan' ? (
  //             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
  //               {subscriptionDetails?.trialEndDate && (
  //                 <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.info.main }}>
  //                   Expires on {new Date(subscriptionDetails.trialEndDate).toLocaleDateString()}
  //                 </Typography>
  //               )}
  //               <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-end' }, mt: 0.5 }}>
  //                 {subscriptionDetails?.created_at && (
  //                   <Typography variant="caption" color="text.secondary">
  //                     Started on {new Date(subscriptionDetails.created_at).toLocaleDateString()}
  //                   </Typography>
  //                 )}
  //                 {subscriptionDetails?.trialEndDate && (
  //                   <Typography variant="caption" sx={{ ml: { xs: 0, sm: 1 }, mt: { xs: 0.5, sm: 0 }, color: theme.palette.success.main, fontWeight: 500 }}>
  //                     {Math.ceil((new Date(subscriptionDetails.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
  //                   </Typography>
  //                 )}
  //               </Box>
  //             </Box>
  //           ) : (
  //             <Button
  //               variant="contained"
  //               color="primary"
  //               onClick={() => navigate('/subscription')}
  //               sx={{
  //                 backgroundColor: theme.palette.success.main,
  //                 '&:hover': {
  //                   backgroundColor: theme.palette.success.dark
  //                 }
  //               }}
  //             >
  //               Upgrade to Premium
  //             </Button>
  //           )}
  //         </Grid>
  //       </Grid>
  //     </CardContent>
  //   </Card>
  // );

  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMobileMenuOpen = (e) => {
    setMobileMenuAnchorEl(e.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  return (
    <>
      <style>{skeletonStyles}</style>
      <MetaTags
        title="Dashboard"
        description="Manage your surveys, view analytics, and create new surveys with our AI-powered platform."
        canonical="/dashboard"
        keywords={[
          'survey dashboard',
          'survey analytics',
          'survey management',
          'AI surveys',
          'survey creation'
        ]}
      />
      <Box sx={{
        bgcolor: theme.palette.background.default,
        minHeight: '100vh',
        pb: 8
      }}>
        {/* Modern AppBar with subtle styling */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderBottom: '1px solid',
            borderColor: 'grey.100'
          }}
        >
          <Toolbar sx={{ minHeight: '64px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <img
                src={logo}
                alt="SurveySlack Logo"
                style={{
                  height: '32px',
                  marginRight: '12px'
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: '1.25rem',
                  letterSpacing: '-0.025em'
                }}
              >
                SurveySlack
              </Typography>
            </Box>

            {!isSmallScreen ? (
              <>
                {/* Documentation Link */}
                <Box sx={{ mx: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DocumentationIcon />}
                    onClick={() => navigate('/documentations')}
                    size="small"
                    color="primary"
                  >
                    Documentation
                  </Button>
                </Box>

                {/* Bug Report Button */}
                <Box sx={{ mx: 1 }}>
                  <BugReportButton size="small" />
                </Box>
              </>
            ) : (
              <IconButton
                size="small"
                onClick={handleMobileMenuOpen}
                aria-controls={mobileMenuAnchorEl ? 'mobile-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={mobileMenuAnchorEl ? 'true' : undefined}
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{
                ml: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.875rem'
                }}
              >
                {currentUser?.email?.[0].toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu
              id="mobile-menu"
              anchorEl={mobileMenuAnchorEl}
              open={Boolean(mobileMenuAnchorEl)}
              onClose={handleMobileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  navigate('/documentations');
                  handleMobileMenuClose();
                }}
              >
                <ListItemIcon>
                  <DocumentationIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Documentation</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  navigate('/report-issue');
                  handleMobileMenuClose();
                }}
              >
                <ListItemIcon>
                  <BugReportIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Report Issue</ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: 2
            }
          }}
        >
          <MenuItem onClick={() => {
            handleProfileMenuClose();
            navigate('/profile');
          }}>
            <PersonIcon fontSize="small" sx={{ mr: 1.5 }} />
            Profile
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
            Logout
          </MenuItem>
        </Menu>

        {/* <Container sx={{ mt: 9, px: { xs: 2, sm: 3 ,md:4},maxWidth: '90%' }}> */}
        <Container maxWidth={false} sx={{ mt: { xs: '12%',  md: '8%',lg:'5%' }, px: { xs: 2, sm: 3, md: 4 }, width: '90%', mx: 'auto' }}>

          {/* Welcome Section with top action buttons */}
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  mb: 0.5
                }}
              >
                Welcome back, {currentUser?.email?.split('@')[0]}!
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary
                }}
              >
                Create, manage, and analyze your surveys with ease
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Button
                variant="contained"
                size={isSmallScreen ? 'small' : 'medium'}
                startIcon={<AddIcon />}
                onClick={handleCreateSurvey}
                sx={{
                  py: { xs: 0.5, sm: 1 },
                  px: { xs: 1, sm: 2 },
                  // borderRadius: 2,
                  backgroundColor: theme.palette.primary.main,
                  fontSize: { xs: '0.85rem', sm: '1rem' }
                }}
              >
                Create New Survey
              </Button>

              <Button
                variant="outlined"
                size={isSmallScreen ? 'small' : 'medium'}
                startIcon={<TemplateIcon />}
                onClick={() => navigate('/surveys/templates')}
                sx={{
                  py: { xs: 0.5, sm: 1 },
                  px: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.85rem', sm: '1rem' }
                }}
              >
                Browse Templates
              </Button>

              <Button
                variant="outlined"
                size={isSmallScreen ? 'small' : 'medium'}
                startIcon={<AIIcon />}
                onClick={handleCreateWithAI}
                sx={{
                  py: { xs: 0.5, sm: 1 },
                  px: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.85rem', sm: '1rem' }
                }}
              >
                Create with AI
              </Button>
            </Stack>
          </Box>

          {/* Analytics cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 14 }}>Total Surveys</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: 32 }}> {surveys.length || 0} </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 14 }}>Active Surveys</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: 32 }}> {surveys.filter(s => s.status === 'published').length || 0} </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 14 }}>Total Responses</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: 32 }}> {totalCompletedResponses} </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 14 }}>Avg Response Rate</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: 32 }}> {calculateAvgResponseRate(surveys)}% </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          





          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
              Recent Surveys
            </Typography>

            
            <Stack direction="row" spacing={1} alignItems="center" flexWrap='wrap' sx={{ ml: 'auto' }}>
              <div style={{ position: 'relative', minWidth: 220 }}>
                <SearchIcon fontSize="small" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: theme.palette.text.secondary }} />
                <input
                  type="search"
                  aria-label="Search surveys"
                  placeholder="Search surveys..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    height: 32,
                    padding: '6px 10px 6px 34px',
                   borderRadius: 6,
                    border: '1px solid rgba(0,0,0,0.12)',
                    fontSize: '0.85rem',
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ position: 'relative', width: 160 }}>
                <select
                  aria-label="Filter surveys"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{
                    width: '100%',
                    height: 32,
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid rgba(0,0,0,0.12)',
                    fontSize: '0.85rem',
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="all">All Surveys</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
                <FilterListIcon fontSize="small" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: theme.palette.text.secondary }} />
              </div>

              {/* <Box sx={{ display: 'flex', alignItems: 'center' }}> */}
              <Paper
                elevation={0}
                sx={{
                  mt: { xs: 2, sm: 0 },
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: '4px',
                   borderRadius: 1,
                  bgcolor: theme.palette.background.paper,
                  border: '1px solid',
                  borderColor: 'grey.100',
                  boxShadow: 'none'
                }}
              >
                <IconButton
                  size="small"
                  color={viewMode === 'grid' ? 'primary' : 'default'}
                  onClick={() => setViewMode('grid')}
                  title="Grid view"
                >
                  <AppsIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={viewMode === 'list' ? 'primary' : 'default'}
                  onClick={() => setViewMode('list')}
                  title="List view"
                >
                  <ViewListIcon fontSize="small" />
                </IconButton>
              </Paper>
            </Stack>
          </Box>
          {loading ? (
            <Grid container spacing={2}>
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <Card
                    sx={{
                      // borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                      border: '1px solid rgba(0, 0, 0, 0.06)',
                      overflow: 'hidden'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Skeleton
                          variant="text"
                          width="70%"
                          height={24}
                          sx={{
                            animation: 'pulse 1.5s ease-in-out infinite'
                          }}
                        />
                        <Skeleton
                          variant="rectangular"
                          width={60}
                          height={24}
                          sx={{
                            borderRadius: 1,
                            animation: 'pulse 1.5s ease-in-out 0.2s infinite'
                          }}
                        />
                      </Box>

                      <Skeleton
                        variant="text"
                        width="50%"
                        height={16}
                        sx={{
                          mb: 2,
                          animation: 'pulse 1.5s ease-in-out 0.4s infinite'
                        }}
                      />

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {[1, 2, 3, 4, 5].map((iconItem) => (
                          <Skeleton
                            key={iconItem}
                            variant="circular"
                            width={32}
                            height={32}
                            sx={{
                              animation: `pulse 1.5s ease-in-out ${iconItem * 0.1}s infinite`
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : surveys.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              No surveys created yet. Click "Create Survey" to get started!
            </Typography>
          ) : (
            viewMode === 'grid' ? (
              <Grid container spacing={2}>
                {filteredSurveys.map((survey) => (
                  <Grid item xs={12} sm={6} md={4} key={survey.id}>
                    <Card
                      sx={{

                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle1" gutterBottom noWrap sx={{ fontWeight: 500, pr: 2, minWidth: 0 }}>
                            {decodeHtmlEntities(survey.name)}
                          </Typography>
                          <Paper
                            sx={{
                              px: 1.25,
                              py: 0.4,
                              // borderRadius: 1,
                              // bgcolor: getStatusColor(survey.status).bg,
                              bgcolor: (theme) => {
                                // use a subtle, low-opacity background based on status
                                if (survey.status === 'published') return alpha(theme.palette.success.main, 0.08);
                                if (survey.status === 'draft') return alpha('#F5F5F5', 0.75);
                                return alpha(theme.palette.warning.main, 0.08);
                              },
                              // border: '1px solid',
                              // borderColor: getStatusColor(survey.status).border,
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 500,
                                // color: theme.palette.success.main,
                                color: (theme) => {
                                  // use a subtle, low-opacity background based on status
                                  if (survey.status === 'published') return theme.palette.success.main;
                                  if (survey.status === 'draft') return theme.palette.text.secondary;
                                  return theme.palette.warning.main;
                                },

                                letterSpacing: '0.2px'
                              }}
                            >
                              {survey.status.charAt(0).toUpperCase() + survey.status.slice(1)}
                            </Typography>
                          </Paper>
                        </Box>


                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: '4%' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                            <ResponsesIcon fontSize="small" sx={{ fontSize: 16 }} />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {`${survey.completed_responses_count || 0} responses`}
                            </Typography>
                          </Box>

                          {survey.status === 'published' && survey.updated_at && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                              <CalendarIcon fontSize="small" sx={{ fontSize: 16 }} />
                              <Typography component="span" variant="caption" color="text.secondary">
                                {formatDate(survey.updated_at)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{mb: '2%',  height: 2, bgcolor: 'grey.100' }} />

                        {/* action buttons */}
                        <Stack direction="row" justifyContent="center" flexWrap="wrap" width='100%' >
                          {isCompactActions ? (
                            <>
                              <IconButton size="small" aria-label="Edit survey" onClick={() => navigate(`/survey/edit/${survey.id}`)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" aria-label="Share survey" onClick={() => handleShareClick(survey)}>
                                <ShareIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" aria-label="View results" onClick={() => navigate(`/survey/responses/${survey.id}`)}>
                                <AnalyticsIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" aria-label="Clone survey" onClick={() => { setCloneSourceId(survey.id); setCloneDialogOpen(true); }} title="Clone survey">
                                <CloneIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" aria-label="Delete survey" onClick={() => handleDeleteClick(survey)} sx={{ color: 'error.main' }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(`/survey/edit/${survey.id}`)}
                                onKeyDown={(e) => (e.key === 'Enter' ? navigate(`/survey/edit/${survey.id}`) : null)}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  minWidth: 88,
                                  px: 0.5,
                                  py: 0.5,
                                  color: 'text.secondary',
                                  cursor: 'pointer',
                                  textTransform: 'none',
                                  '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 }
                                }}
                              >
                                <EditIcon fontSize="small" />
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Edit</Typography>
                              </Box>
                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => handleShareClick(survey)}
                                onKeyDown={(e) => (e.key === 'Enter' ? handleShareClick(survey) : null)}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  minWidth: 88,
                                  px: 0.5,
                                  py: 0.5,
                                  color: 'text.secondary',
                                  cursor: 'pointer',
                                  textTransform: 'none',
                                  '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 }
                                }}
                              >
                                <ShareIcon fontSize="small" />
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Share</Typography>
                              </Box>
                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(`/survey/responses/${survey.id}`)}
                                onKeyDown={(e) => (e.key === 'Enter' ? navigate(`/survey/responses/${survey.id}`) : null)}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  minWidth: 88,
                                  px: 0.5,
                                  py: 0.5,
                                  color: 'text.secondary',
                                  cursor: 'pointer',
                                  textTransform: 'none',
                                  '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 }
                                }}
                              >
                                <AnalyticsIcon fontSize="small" />
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Results</Typography>
                              </Box>
                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => { setCloneSourceId(survey.id); setCloneDialogOpen(true); }}
                                onKeyDown={(e) => (e.key === 'Enter' ? (setCloneSourceId(survey.id), setCloneDialogOpen(true)) : null)}
                                title="Clone survey"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  minWidth: 88,
                                  px: 0.5,
                                  py: 0.5,
                                  color: 'text.secondary',
                                  cursor: 'pointer',
                                  textTransform: 'none',
                                  '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 }
                                }}
                              >
                                <CloneIcon fontSize="small" />
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Clone</Typography>
                              </Box>
                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => handleDeleteClick(survey)}
                                onKeyDown={(e) => (e.key === 'Enter' ? handleDeleteClick(survey) : null)}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  minWidth: 88,
                                  px: 0.5,
                                  py: 0.5,
                                  color: 'error.main',
                                  cursor: 'pointer',
                                  textTransform: 'none',
                                  '&:hover': { backgroundColor: 'error.light', borderRadius: 1 }
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Delete</Typography>
                              </Box>
                            </>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Stack spacing={2}>
                {filteredSurveys.map((survey) => (
                  <Paper key={survey.id} sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                      {/* <Paper
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 1,
                          bgcolor: getStatusColor(survey.status).bg,
                          border: '1px solid',
                          borderColor: getStatusColor(survey.status).border,
                          mr: 1
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 500, color: getStatusColor(survey.status).color }}>
                          {survey.status.charAt(0).toUpperCase() + survey.status.slice(1)}
                        </Typography>
                      </Paper> */}
                       <Paper
                            sx={{
                              px: 1.25,
                              py: 0.4,
                              // borderRadius: 1,
                              // bgcolor: getStatusColor(survey.status).bg,
                              bgcolor: (theme) => {
                                // use a subtle, low-opacity background based on status
                                if (survey.status === 'published') return alpha(theme.palette.success.main, 0.08);
                                if (survey.status === 'draft') return alpha('#F5F5F5', 0.75);
                                return alpha(theme.palette.warning.main, 0.08);
                              },
                              // border: '1px solid',
                              // borderColor: getStatusColor(survey.status).border,
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 500,
                                // color: theme.palette.success.main,
                                color: (theme) => {
                                  // use a subtle, low-opacity background based on status
                                  if (survey.status === 'published') return theme.palette.success.main;
                                  if (survey.status === 'draft') return theme.palette.text.secondary;
                                  return theme.palette.warning.main;
                                },

                                letterSpacing: '0.2px'
                              }}
                            >
                              {survey.status.charAt(0).toUpperCase() + survey.status.slice(1)}
                            </Typography>
                          </Paper>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                          {decodeHtmlEntities(survey.name)}
                        </Typography>
                        {/* <Typography variant="caption" color="text.secondary">
                          {survey.completed_responses_count || 0} responses {survey.status === 'published' && survey.updated_at ? `• ${formatDate(survey.updated_at)}` : ''}
                        </Typography> */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: '4%' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                            <ResponsesIcon fontSize="small" sx={{ fontSize: 16 }} />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {`${survey.completed_responses_count || 0} responses`}
                            </Typography>
                          </Box>

                          {survey.status === 'published' && survey.updated_at && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                              <CalendarIcon fontSize="small" sx={{ fontSize: 16 }} />
                              <Typography component="span" variant="caption" color="text.secondary">
                                {formatDate(survey.updated_at)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Box>
                      <Stack direction="row" spacing={1}>
                        {isCompactActions ? (
                          <>
                            <IconButton size="small" aria-label="Edit survey" onClick={() => navigate(`/survey/edit/${survey.id}`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label="Share survey" onClick={() => handleShareClick(survey)}>
                              <ShareIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label="View results" onClick={() => navigate(`/survey/responses/${survey.id}`)}>
                              <AnalyticsIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label="Clone survey" onClick={() => { setCloneSourceId(survey.id); setCloneDialogOpen(true); }} title="Clone survey">
                              <CloneIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label="Delete survey" onClick={() => handleDeleteClick(survey)} sx={{ color: 'error.main' }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <Box
                              role="button"
                              tabIndex={0}
                              onClick={() => navigate(`/survey/edit/${survey.id}`)}
                              onKeyDown={(e) => (e.key === 'Enter' ? navigate(`/survey/edit/${survey.id}`) : null)}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 88,
                                px: 0.5,
                                py: 0.5,
                                color: 'text.secondary',
                                cursor: 'pointer',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 }
                              }}
                            >
                              <EditIcon fontSize="small" />
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Edit</Typography>
                            </Box>
                            <Box
                              role="button"
                              tabIndex={0}
                              onClick={() => handleShareClick(survey)}
                              onKeyDown={(e) => (e.key === 'Enter' ? handleShareClick(survey) : null)}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 88,
                                px: 0.5,
                                py: 0.5,
                                color: 'text.secondary',
                                cursor: 'pointer',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 }
                              }}
                            >
                              <ShareIcon fontSize="small" />
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Share</Typography>
                            </Box>
                            <Box
                              role="button"
                              tabIndex={0}
                              onClick={() => navigate(`/survey/responses/${survey.id}`)}
                              onKeyDown={(e) => (e.key === 'Enter' ? navigate(`/survey/responses/${survey.id}`) : null)}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 88,
                                px: 0.5,
                                py: 0.5,
                                color: 'text.secondary',
                                cursor: 'pointer',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 }
                              }}
                            >
                              <AnalyticsIcon fontSize="small" />
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Results</Typography>
                            </Box>
                            <Box
                              role="button"
                              tabIndex={0}
                              onClick={() => { setCloneSourceId(survey.id); setCloneDialogOpen(true); }}
                              onKeyDown={(e) => (e.key === 'Enter' ? (setCloneSourceId(survey.id), setCloneDialogOpen(true)) : null)}
                              title="Clone survey"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 88,
                                px: 0.5,
                                py: 0.5,
                                color: 'text.secondary',
                                cursor: 'pointer',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 }
                              }}
                            >
                              <CloneIcon fontSize="small" />
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Clone</Typography>
                            </Box>
                            <Box
                              role="button"
                              tabIndex={0}
                              onClick={() => handleDeleteClick(survey)}
                              onKeyDown={(e) => (e.key === 'Enter' ? handleDeleteClick(survey) : null)}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 88,
                                px: 0.5,
                                py: 0.5,
                                color: 'error.main',
                                cursor: 'pointer',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: 'error.light', borderRadius: 1 }
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Delete</Typography>
                            </Box>
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )
          )}
        </Container>
      </Box>

      {/* Create Survey Modal */}
      <CreateSurveyModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSelect={handleSurveyOptionSelect}
      />

      {/* Theme Selection Modal */}
      <SurveyThemeModal
        open={themeModalOpen}
        onClose={() => setThemeModalOpen(false)}
        onThemeSelect={handleThemeSelect}
      />

      {/* Clone Survey Dialog */}
      <Dialog
        open={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 2 } }}
      >
        <DialogTitle>Clone Survey</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            Select a survey to clone. You'll be taken to the survey creation page with the selected survey as the source.
          </Typography>
          <TextField
            select
            fullWidth
            value={cloneSourceId}
            onChange={(e) => setCloneSourceId(e.target.value)}
            label="Select survey to clone"
            variant="outlined"
          >
            {surveys.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {`${decodeHtmlEntities(s.name)}(copy)`}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>{t('common:buttons.cancel') || 'Cancel'}</Button>
          <Button
            variant="contained"
            onClick={handleCloneConfirm}
            disabled={!cloneSourceId || cloneLoading}
          >
            {cloneLoading ? <CircularProgress size={18} color="inherit" /> : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Name Dialog */}
      <Dialog
        open={nameModalOpen}
        onClose={() => setNameModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        <DialogTitle>{t('dashboard:modals.nameSurvey.title')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('dashboard:modals.nameSurvey.label')}
            type="text"
            fullWidth
            variant="outlined"
            value={surveyName}
            onChange={(e) => setSurveyName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameModalOpen(false)}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleCreateNewSurvey}
            variant="contained"
            disabled={!surveyName.trim()}
          >
            {t('dashboard:modals.nameSurvey.button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Survey</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{surveyToDelete?.name}"? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Toast Component */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>

      {/* Add Share Dialog */}
      <ShareSurveyDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setSelectedSurvey(null);
        }}
        survey={{
          ...selectedSurvey,
          user_id: currentUser?.uid // Ensure user_id is always passed
        }}
      />
    </>
  );
};

export default Dashboard;